<template>
  <div
    v-if="root.children.length || root.isLast"
    :style="{ minHeight, height, maxHeight }"
    class="b-ate-tree-form"
  >
    <div v-if="!readonly && !searchless" class="b-ate-tree-form__top">
      <slot
        name="search"
        :attrs="{ loading: isLoadingSearch, autofocus, value: search, name }"
        :on="{ input: onSearchDebounce }"
      >
        <BTextField
          v-model="search"
          :loading="isLoadingSearch"
          :name="name"
          :autofocus="autofocus"
          label="Поиск"
          class="mb-1"
          @input="onSearchDebounce"
        />
      </slot>

      <v-checkbox
        v-if="multiple"
        :input-value="root.isSelected"
        :loading="root.loading"
        :label="parent_mode ? 'Выбрать все' : 'Выбрать все регионы'"
        dense
        :disabled="Boolean(rootSearch)"
        :style="rootSearch && { opacity: 0.25 }"
        :ripple="false"
        hide-details
        class="pa-0 my-0 mb-4 b-ate-tree-form__checkbox"
        @change="onChange(root)"
      />

      <div
        v-if="search && !childrenNode.length"
        class="b-ate-tree-form__message-empty"
        v-text="'Не найдено'"
      />

      <div
        v-else-if="root.isLast"
        v-text="'Список регионов пуст'"
      />
    </div>

    <div v-else-if="!searchless">
      <slot name="before-counts" />

      <slot name="counts">
        <BAteTreeListCount
          :skeleton="skeleton"
          :regions="regions"
          :municipalities="municipalities"
          :schools="schools"
          :parent_mode="parent_mode"
          class="mb-4"
        />
      </slot>

      <v-divider
        v-if="!(isEmptyAtes && readonly)"
        class="mt-2"
      />
    </div>

    <div ref="scroll-container" class="b-ate-tree-form__bottom">
      <slot
        name="tree"
        :root="root"
        :root-search="rootSearch"
        :load-children="onLoadChildren"
        :on="{
          loadChildren: onLoadChildren,
          change: onChange,
          clear: onClear,
        }"
      >
        <BAteTreeFormRecursive
          v-for="node in childrenNode"
          :key="node.data.code"
          :node="node"
          :load-children="onLoadChildren"
          :readonly="readonly"
          :clearable="clearable"
          :max-level="maxLevel"
          :class="{
            'js-indeterminate': node.isIndeterminate,
            'js-selected': node.isSelected,
          }"
          :parent_mode="parent_mode"
          @change="onChange"
          @clear="onClear"
        />
      </slot>
    </div>
  </div>

  <div v-else class="mx-8">
    <v-skeleton-loader :loading="skeleton" type="button">
      <v-input
        :error="error"
        :error-messages="errorMessages"
      >
        <v-btn
          :loading="isLoading"
          depressed
          small
          block
          :text="isLoading"
          @click="onLoadChildren(root)"
        >
          Загрузить дерево ATE
        </v-btn>
      </v-input>
    </v-skeleton-loader>
  </div>
</template>

<script>
/* eslint no-param-reassign: 0 */

import Vue from 'vue'
import { CancelToken, isCancel } from 'axios'
import mapValues from 'lodash/mapValues'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import debounce from 'lodash/debounce'
import memoize from 'lodash/memoize'
import {
  AteRegionsApi,
  AteMunicipalitiesApi,
  AteSchoolsApi,
} from '@/api/resources'
import { DEBOUNCE_TIMEOUT } from '@/helpers/enums/params'
import { RootTreeNode, IS_LAST } from './utils'

import BTextField from '@/components/forms/BTextField.vue'
import BAteTreeFormRecursive from './BAteTreeFormRecursive.vue'
import BAteTreeListCount from './BAteTreeListCount.vue'


const PER_PAGE = 200

export const LEVELS = {
  ROOT: 0,
  REGIONS: 1,
  MUNICIPALITIES: 2,
  SCHOOLS: 3,
}

export {
  RootTreeNode,
  BAteTreeFormRecursive,
  BAteTreeListCount,
}

export const createRootNode = () => (
  new RootTreeNode({ name: 'Дерево ATE', id: 'root' })
)

function resolverMemoize(params) {
  return [
    JSON.stringify(params),
    this.model || void 0,
    this.permission || void 0,
  ].filter(Boolean).join('###')
}

export default {
  name: 'BAteTreeForm',
  $_veeValidate: {
    value() {
      return [].concat(
        this.regions,
        this.municipalities,
        this.schools,
      )
    },
    name() {
      return this.name
    },
  },
  components: {
    BTextField,
    BAteTreeFormRecursive,
    BAteTreeListCount,
  },
  props: {
    parent_mode: Boolean,
    skeleton: Boolean,
    minHeight: String,
    height: String,
    maxHeight: String,
    error: Boolean,
    errorMessages: String,
    name: String,
    readonly: Boolean,
    clearable: Boolean,
    multiple: Boolean,
    autofocus: Boolean,
    searchless: Boolean,
    memoizeless: Boolean,
    regions: {
      type: Array,
      required: true,
    },
    municipalities: {
      type: Array,
      required: true,
    },
    schools: {
      type: Array,
      required: true,
    },
    model: {
      type: String,
      required: true,
      validator: (prop) => ['Group', 'User', 'Roadmap', ''].includes(prop),
    },
    permission: {
      type: String,
      default: 'edit',
    },
    maxLevel: {
      type: Number,
      default: LEVELS.SCHOOLS,
    },
    defaultCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isLoading: false,
    search: '',
    isLoadingSearch: false,
    root: createRootNode(),
    rootSearch: null,
    get regionsApi() {
      return new AteRegionsApi()
    },
    get municipalitiesApi() {
      return new AteMunicipalitiesApi()
    },
    get schoolsApi() {
      return new AteSchoolsApi()
    },
  }),
  computed: {
    selectedIdsMap() {
      const map = {
        [LEVELS.ROOT]: { currentIds: [], indeterminateIds: [] },
        [LEVELS.REGIONS]: { currentIds: [], indeterminateIds: [] },
        [LEVELS.MUNICIPALITIES]: { currentIds: [], indeterminateIds: [] },
        [LEVELS.SCHOOLS]: { currentIds: [], indeterminateIds: [] },
      }

      if (this.regions.length === this.root.children.length) {
        map[LEVELS.ROOT].currentIds.push('root')
      }

      this.regions.forEach((_) => {
        map[LEVELS.REGIONS].currentIds.push(_.id)
      })

      this.municipalities.forEach((_) => {
        map[LEVELS.MUNICIPALITIES].currentIds.push(_.id)
        map[LEVELS.ROOT].indeterminateIds.push('root')
        map[LEVELS.REGIONS].indeterminateIds.push(_.region_id)
      })

      this.schools.forEach((_) => {
        map[LEVELS.SCHOOLS].currentIds.push(_.id)
        map[LEVELS.ROOT].indeterminateIds.push('root')
        map[LEVELS.MUNICIPALITIES].indeterminateIds.push(_.municipality_id)
        map[LEVELS.REGIONS].indeterminateIds.push(_.region_id)
      })

      return mapValues(map, (value) => mapValues(value, uniq))
    },
    rootView() {
      const result = this.getPartlyTree(this.regions, this.municipalities, this.schools)
      return Vue.observable(result)
    },
    childrenNode() {
      if (this.readonly) return this.rootView.children
      const node = this.rootSearch || this.root
      return node.children
    },
    isEmptyAtes() {
      return (
        this.regions.length
        || this.municipalities.length
        || this.schools.length
      ) === 0
    },
  },
  watch: {
    selectedIdsMap: {
      // immediate: true,
      handler(value) {
        void this.root?.setSelectedIdsMap(value)
        void this.rootSearch?.setSelectedIdsMap?.(value)
      },
    },
  },
  created() {
    this.onSearchDebounce = debounce(
      this.onSearch,
      DEBOUNCE_TIMEOUT,
    )

    if (!this.memoizeless) {
      this.getRegions = memoize(this.getRegions, resolverMemoize)
      this.getMunicipalities = memoize(this.getMunicipalities, resolverMemoize)
      this.getSchools = memoize(this.getSchools, resolverMemoize)
      this.getAtes = memoize(this.getAtes)
    }

    // Загружаем при старте
    this.onLoadChildren(this.root)

    if (!this.multiple) {
      this.$watch('childrenNode', async () => {
        await this.$nextTick()
        const activeElement = (
          this.$el.querySelector('.js-selected')
          || this.$el.querySelector('.js-indeterminate')
        )

        if (activeElement) {
          const module = await import(
            /* webpackChunkName: "vue-scrollto" */
            'vue-scrollto'
          )

          const vueScrollTo = module.default

          vueScrollTo.scrollTo(activeElement, 200, {
            container: this.$refs['scroll-container'],
          })
        }
      })
    }
  },
  beforeDestroy() {
    // this.cleanupCache() - оставим кеш
  },
  methods: {
    /** @param {import('./utils').TreeNode} node */
    async onLoadChildren(node) {
      if (node.isLast && node.level !== LEVELS.ROOT) return

      switch (node.level) {
        case LEVELS.ROOT: {
          const data = await this.getRegions({})
          node.setChildren(data)
          node.isLast = data.length ? false : IS_LAST
          break
        }

        case LEVELS.REGIONS: {
          const data = await this.getMunicipalities({ id: node.data.id })
          node.setChildren(data)
          node.isLast = data.length ? false : IS_LAST
          break
        }

        case LEVELS.MUNICIPALITIES: {
          const data = await this.getSchools({ id: node.data.id })
          node.setChildren(data, IS_LAST)
          node.isLast = data.length ? false : IS_LAST
          break
        }

        default: {
          const message = `[${this.$options.name}]: Уровень "${node.level}" не поддерживается`
          // eslint-disable-next-line no-console
          console.warn(message)
        }
      }
    },
    async getRegions({ search }, config) {
      this.isLoading = true
      const params = {
        pageSize: PER_PAGE,
        model: this.model || void 0,
        permission: this.permission,
        search,
      }

      const api = this.regionsApi

      try {
        const { data } = await api.getAll(params, config)
        return data
      } catch (e) {
        setTimeout(this.cleanupCache)
        if (!isCancel(e)) {
          this.$notice.errors(e)
        }
        return Promise.reject(e)
      } finally {
        this.isLoading = false
      }
    },
    async getMunicipalities({ id, search }, config) {
      const params = {
        pageSize: PER_PAGE,
        region_id: id,
        model: this.model || void 0,
        permission: this.permission,
        search,
      }

      const api = this.municipalitiesApi

      try {
        const { data } = await api.getAll(params, config)
        return data
      } catch (e) {
        setTimeout(this.cleanupCache)
        if (!isCancel(e)) {
          this.$notice.errors(e)
        }
        return Promise.reject(e)
      }
    },
    async getSchools({ id, search }, config) {
      const params = {
        pageSize: PER_PAGE,
        municipality_id: id,
        model: this.model || void 0,
        permission: this.permission,
        search,
      }

      const api = this.schoolsApi

      try {
        const { data } = await api.getAll(params, config)
        return data
      } catch (e) {
        setTimeout(this.cleanupCache)
        if (!isCancel(e)) {
          this.$notice.errors(e)
        }
        return Promise.reject(e)
      }
    },
    async getAtes(search) {
      void this.cancelTokenSource?.cancel(this.$options.name)
      this.cancelTokenSource = CancelToken.source()
      const cancelToken = this.cancelTokenSource.token

      const params = {
        pageSize: PER_PAGE,
        model: this.model || void 0,
        permission: this.permission,
        search,
      }

      const config = { cancelToken }

      this.isLoadingSearch = true

      const actions = [
        this.getRegions(params, config),
        this.getMunicipalities(params, search),
        this.getSchools(params, config),
      ]

      try {
        const response = await Promise.all(actions)
        return response
      } catch (e) {
        setTimeout(this.cleanupCache)
        if (isCancel(e)) return null
        // this.$notice.errors(e)
        return null
      } finally {
        this.isLoadingSearch = false
      }
    },
    cleanupCache() {
      Object.values(this.$options.methods)
        .forEach((method) => { method.cache?.clear() })
    },
    async onSearch(search) {
      this.search = search

      if (!search) {
        void this.cancelSource?.cancel(this.$options.name)
        this.rootSearch = null
        return
      }

      const response = await this.getAtes(search)
      if (!response) return
      this.rootSearch = this.getPartlyTree(...response)
    },
    getPartlyTree(regions, municipalities, schools) {
      const regionIds = regions.map((_) => _.id)
      // const municipalityIds = municipalities.map((_) => _.id)

      const allRegions = this.root.$list || []

      let partlyAllRegionIds = [
        regionIds,
        municipalities.map((_) => _.region_id),
        schools.map((_) => _.region_id),
      ].flat()

      partlyAllRegionIds = uniq(partlyAllRegionIds)

      let partlyAllMunicipalities = [
        municipalities,
        schools.map((_) => ({
          ..._.municipality,
          region_id: _.region_id,
        })),
      ].flat()

      partlyAllMunicipalities = uniqBy(partlyAllMunicipalities, 'id')


      //


      const rootPartly = createRootNode()

      const collapsed = this.defaultCollapsed

      let list = allRegions.filter((_) => partlyAllRegionIds.includes(_.id))
      rootPartly.setChildren(list, !IS_LAST)

      rootPartly.children.forEach((node$1) => {
        list = partlyAllMunicipalities.filter((_) => _.region_id === node$1.data.id)
        // node$1.disabled = !regionIds.includes(node$1.data.id)
        node$1.isActive = !collapsed && list.length > 0
        // node$1.isLast = !list.length
        node$1.setChildren(list, !IS_LAST)

        node$1.children.forEach((node$2) => {
          list = schools.filter((_) => _.municipality_id === node$2.data.id)
          // node$2.disabled = !municipalityIds.includes(node$2.data.id)
          node$2.isActive = !collapsed && list.length > 0
          // node$2.isLast = !list.length
          node$2.setChildren(list, IS_LAST)
        })
      })

      rootPartly.setSelectedIdsMap(this.selectedIdsMap)

      return rootPartly
    },
    /** @param {import('./utils').TreeNode} node */
    onChange(node) {
      const { parent_mode } = this

      if (this.multiple) {
        if (parent_mode) {
          this.onChangeMultipleWithParents(node)
        } else {
          this.onChangeMultiple(node)
        }
        return
      }

      const isSelected = !node.isSelected

      const regions = []
      const municipalities = []
      const schools = []

      if (isSelected) {
        switch (node.level) {
          case LEVELS.REGIONS:
            regions.push(node.data)
            break

          case LEVELS.MUNICIPALITIES:
            municipalities.push(node.data)
            break

          case LEVELS.SCHOOLS:
            // при запросе списке нет region, а он нужен для user form
            node.data.region = node.parent.data
            schools.push(node.data)
            break

          default: {
            const message = `[${this.$options.name}]: Уровень "${node.level}" не поддерживается`
            // eslint-disable-next-line no-console
            console.warn(message)
            return
          }
        }
      }

      this.$emit('update:regions', regions)
      this.$emit('update:municipalities', municipalities)
      this.$emit('update:schools', schools)

      // node.toggleSelected()
    },
    /** @param {import('./utils').TreeNode} node */
    onChangeMultiple(node) {
      let type

      node.isSelected = !node.isSelected

      switch (node.level) {
        case LEVELS.ROOT:
          break

        case LEVELS.REGIONS:
          type = 'regions'
          break

        case LEVELS.MUNICIPALITIES:
          type = 'municipalities'
          break

        case LEVELS.SCHOOLS:
          type = 'schools'
          break

        default: {
          const message = `[${this.$options.name}]: Уровень "${node.level}" не поддерживается`
          // eslint-disable-next-line no-console
          console.warn(message)
          return
        }
      }

      const { data, isActive, isSelected } = node

      if (node.level === LEVELS.ROOT) {
        const list = isSelected ? node.$list.slice() : []
        this.$emit('update:regions', list)
      } else {
        const list = isSelected
          ? this[type].concat(data)
          : this[type].filter((_) => _.id !== data.id)

        this.$emit(`update:${type}`, list)
      }

      if (!isActive && node.level !== LEVELS.ROOT) return

      // eslint-disable-next-line no-shadow
      const getList = (node, isSelected, list) => {
        if (isSelected) {
          list = list.concat(node.$list)
          list = uniqBy(list, 'id')
        } else {
          const excludedIds = node.$list.map((_) => _.id)
          list = list.filter((_) => !excludedIds.includes(_.id))
        }

        return list
      }

      switch (node.level) {
        case LEVELS.ROOT: {
          const regions = getList(node, isSelected, this.regions)
          let { municipalities } = this
          let { schools } = this

          node.children.forEach((municipalityNode) => {
            if (!municipalityNode.isActive) return
            municipalities = getList(municipalityNode, isSelected, municipalities)

            municipalityNode.children.forEach((schoolNode) => {
              if (!schoolNode.isActive) return
              schools = getList(schoolNode, isSelected, schools)
            })
          })

          this.$emit('update:regions', regions)
          this.$emit('update:municipalities', municipalities)
          this.$emit('update:schools', schools)
          break
        }

        case LEVELS.REGIONS: {
          const municipalities = getList(node, isSelected, this.municipalities)
          let { schools } = this

          node.children.forEach((_) => {
            if (!_.isActive) return
            schools = getList(_, isSelected, schools)
          })

          this.$emit('update:municipalities', municipalities)
          this.$emit('update:schools', schools)
          break
        }

        case LEVELS.MUNICIPALITIES: {
          const schools = getList(node, isSelected, this.schools)
          this.$emit('update:schools', schools)
          break
        }

        case LEVELS.SCHOOLS:
        default:
      }

      this.$nextTick(() => node.update())
    },
    /** @param {import('./utils').TreeNode} node */
    onChangeMultipleWithParents(node) {
      switch (node.level) {
        case LEVELS.ROOT:
          this.onChangeRootWithParents(node)
          break

        case LEVELS.REGIONS: {
          this.onChangeRegionWithParents(node)
          break
        }

        case LEVELS.MUNICIPALITIES: {
          this.onChangeMunicipalityWithParents(node)
          break
        }

        case LEVELS.SCHOOLS: {
          this.onChangeSchoolWithParents(node)
          break
        }

        default: {
          const message = `[${this.$options.name}]: Уровень "${node.level}" не поддерживается`
          // eslint-disable-next-line no-console
          console.warn(message)
        }
      }
    },
    /** @param {import('./utils').TreeNode} node */
    onChangeRootWithParents(node) {
      const isSelected = !node.isSelected
      const regions = isSelected ? node.$list : []

      this.$emit('update:regions', regions)
      this.$emit('update:municipalities', [])
      this.$emit('update:schools', [])

      node.toggleSelected()
    },
    /** @param {import('./utils').TreeNode} node */
    onChangeRegionWithParents(node) {
      const { data, isSelectedParent } = node
      const isSelected = !node.isSelected

      let regions = []
      let municipalities = []
      let schools = []

      if (isSelected) {
        regions = this.regions.concat(data)
      } else {
        const { id } = data
        regions = this.regions.filter((_) => _.id !== id)
      }

      {
        const { id } = data
        municipalities = this.municipalities.filter((_) => _.region_id !== id)
      }

      {
        const { id } = data
        schools = this.schools.filter((_) => _.region_id !== id)
      }

      this.$emit('update:regions', regions)
      this.$emit('update:municipalities', municipalities)
      this.$emit('update:schools', schools)

      if (isSelectedParent) {
        node.toggleSelected()
      }
    },
    /** @param {import('./utils').TreeNode} node */
    async onChangeMunicipalityWithParents(node) {
      const { data, isSelectedParent } = node
      const isSelected = isSelectedParent ? false : !node.isSelected

      node.loading = true

      let regions = []
      let municipalities = []
      let schools = []

      const parents = { regions: this.root.$list }

      try {
        const actions = [
          this.getMunicipalities({ id: data.region_id }),
          this.getSchools({ id: data.id }),
        ]
        const response = await Promise.all(actions)

        // eslint-disable-next-line prefer-destructuring
        parents.municipalities = response[0]
        // eslint-disable-next-line prefer-destructuring
        parents.schools = response[1]
      } finally {
        node.loading = true
      }

      if (isSelectedParent) {
        const { id } = data
        municipalities = parents.municipalities.filter((_) => _.id !== id)
      } else if (isSelected) {
        municipalities = this.municipalities.concat(data)
      } else {
        const { id } = data
        municipalities = this.municipalities.filter((_) => _.id !== id)
      }

      if (municipalities.length > 0 && parents.municipalities.length === municipalities.length) {
        if (!node.parent.data.is_disabled) {
          municipalities = []
          const { region } = parents.municipalities[0]
          regions = this.regions.concat(region)
        }
      } else {
        const id = data.region_id
        regions = this.regions.filter((_) => _.id !== id)
      }

      {
        const { id } = data
        schools = this.schools.filter((_) => _.municipality_id !== id)
      }

      this.$emit('update:regions', regions)
      this.$emit('update:municipalities', municipalities)
      this.$emit('update:schools', schools)

      node.loading = false

      if (isSelectedParent) {
        node.toggleSelected()
      }
    },
    /** @param {import('./utils').TreeNode} node */
    async onChangeSchoolWithParents(node) {
      const { data, isSelectedParent } = node
      const isSelected = isSelectedParent ? false : !node.isSelected

      node.loading = true

      let regions = []
      let municipalities = []
      let schools = []

      const parents = { regions: this.root.$list }

      try {
        const actions = [
          this.getMunicipalities({ id: data.region_id }),
          this.getSchools({ id: data.municipality_id }),
        ]
        const response = await Promise.all(actions)

        // eslint-disable-next-line prefer-destructuring
        parents.municipalities = response[0]
        // eslint-disable-next-line prefer-destructuring
        parents.schools = response[1]
      } finally {
        node.loading = true
      }

      if (isSelectedParent) {
        const { id } = data
        schools = parents.schools.filter((_) => _.id !== id)
      } else if (isSelected) {
        schools = this.schools.concat(data)
      } else {
        const { id } = data
        schools = this.schools.filter((_) => _.id !== id)
      }

      if (schools.length > 0 && parents.schools.length === schools.length) {
        if (!node.parent.data.is_disabled) {
          schools = []
          const municipality = { ...data.municipality, region_id: data.region_id }
          municipalities = this.municipalities.concat(municipality)
        }
      } else {
        const id = data.municipality_id
        municipalities = this.municipalities.filter((_) => _.id !== id)
      }

      if (parents.municipalities.length === municipalities.length) {
        if (!node.parent.parent.data.is_disabled) {
          municipalities = []
          const { region } = parents.municipalities[0]
          regions = this.regions.concat(region)
        }
      } else {
        regions = this.regions.slice()
      }

      if (node.parent.isSelectedParent) {
        let id = data.municipality_id
        municipalities = parents.municipalities.filter((_) => _.id !== id)

        id = data.region_id
        regions = this.regions.filter((_) => _.id !== id)
      }

      this.$emit('update:regions', regions)
      this.$emit('update:municipalities', municipalities)
      this.$emit('update:schools', schools)

      node.loading = false

      if (isSelectedParent) {
        node.toggleSelected()
      }
    },
    /** @param {import('./utils').TreeNode} node */
    onClear(node) {
      let type
      let list

      switch (node.level) {
        case LEVELS.REGIONS:
          type = 'regions'
          list = this.regions
          break

        case LEVELS.MUNICIPALITIES:
          type = 'municipalities'
          list = this.municipalities
          break

        case LEVELS.SCHOOLS:
          type = 'schools'
          list = this.schools
          break

        default: {
          const message = `[${this.$options.name}]: Уровень "${node.level}" не поддерживается`
          // eslint-disable-next-line no-console
          console.warn(message)
          return
        }
      }

      const { id } = node.data
      list = list.filter((_) => _.id !== id)
      this.$emit(`update:${type}`, list)
    },
  },
}
</script>

<style lang="scss">

.b-ate-tree-form {
  display: flex;
  flex-direction: column;

  &__top,
  &__bottom {
    width: 100%;
  }

  &__top {
    padding: 0 7px;
  }

  &__bottom {
    height: 100%;
    overflow-y: auto;
  }

  &__checkbox label {
    margin-left: 4px;
    font-size: 12px;
  }

  &__message-empty {
    margin-top: 20px;
  }

  .v-list-item__title {
    white-space: normal;
  }
}
</style>
